import {
  DataVariants,
  FieldSchema,
  GridFilterTypes,
  GridRenderSlots,
  Schema,
  SchemaClient,
  prepareEquipmentSchema
} from "@digitallab/grid-common-lib";

/**
 * booking-frontend's equipment schema: the shared equipment schema, plus the columns this app
 * shows that the shared one does not know about, plus the handful of places booking deliberately
 * behaves differently.
 *
 * Why a local extension rather than hand-written flag lists: `Schema` is an open
 * `Record<string, FieldSchema>`, so a consumer can add and override fields freely. That keeps every
 * grid flag derived from one place instead of spread across eleven `listOf…` arrays in
 * `constants.js` that have to be kept in step by hand.
 */

/**
 * Columns that exist only in booking. Four are booking-domain concepts the equipment schema has no
 * business knowing; the fifth is derived.
 *
 * Visibility is not declared here — `BOOKING_DEFAULT_VISIBLE` below owns it for every column, these
 * included, so a `defaultVisible` on a field would be silently overwritten.
 *
 * The `grid` blocks below reproduce exactly the flags these columns have in `constants.js` today —
 * this is a refactor, not a behaviour change. `displayed: true` is required for a field to be
 * emitted as a column at all, and `label` for the header to read as anything other than the raw
 * field name: `resolveHeaderName` falls back through `display.label` and `form.label` to `name`,
 * and these fields have neither aspect. Each label is the one `DATA_MODEL_TABLE` already used.
 */
const bookingOnlyFields: Schema = {
  /**
   * Booking checklists attached to an equipment. A cell holds a list, so it is not sortable and
   * carries no filter, and it is excluded from quick search.
   */
  checklists: {
    name: "checklists",
    grid: {
      label: "Checklists",
      displayed: true,
      order: 0,
      sortable: false,
      filter: GridFilterTypes.NONE,
      ignoredForSearch: true
    }
  },

  /**
   * Who may book the equipment. Filtered as a set, but through its own values rather than master
   * data — `getColumnObj` returns early for this key and supplies `ACCESS_CONTROL_FILTER_VALUES`
   * itself, so the filter category here is never consulted. Declared as NONE to keep it out of the
   * derived set-filter list and leave that early return the single owner of its filter.
   */
  bookingAccessControl: {
    name: "bookingAccessControl",
    grid: {
      label: "Access control",
      displayed: true,
      order: 2,
      filter: GridFilterTypes.NONE
    }
  },

  /** Live availability, computed client-side. No filter — there is nothing stable to filter on. */
  instrumentAvailability: {
    name: "instrumentAvailability",
    grid: {
      label: "Booking availability",
      displayed: true,
      order: 3,
      filter: GridFilterTypes.NONE
    }
  },

  /** Monitoring status, from the monitoring integration rather than the equipment record. */
  instrumentStatus: {
    name: "instrumentStatus",
    grid: {
      label: "Monitoring status",
      displayed: true,
      order: 4
    }
  },

  /**
   * A client-side union of the schema's own `qualificationStatus` and `controlledEquipmentStatus`
   * (see `LoadEquipmentService`), presented as one column. Set-filtered from master data and kept
   * out of quick search, since the underlying index has no such field.
   */
  displayQualificationStatus: {
    name: "displayQualificationStatus",
    data: { variant: DataVariants.ENUM, options: [] },
    grid: {
      label: "Qualification / Calibration status",
      displayed: true,
      order: 5,
      filter: GridFilterTypes.SET,
      ignoredForSearch: true
    }
  }
};

/**
 * Which columns the grid shows before a user has saved a layout of their own.
 *
 * `grid.defaultVisible` was authored for instrument-repository's default view, and booking's is a
 * different job: what matters when picking equipment to book is whether it is available, where it
 * is and what state it is in. Taking the shared default unchanged would drop nine of the twelve
 * columns booking shows today — status, availability, location among them — for every user without
 * a saved layout and for anyone who uses Reset All.
 *
 * This is the set `DEFAULT_COLUMNS` was hand-written with, so the default view is unchanged by the
 * move to a derived column list. Ordering still comes from the schema.
 */
const BOOKING_DEFAULT_VISIBLE = [
  "bookingAccessControl",
  "checklists",
  "displayQualificationStatus",
  "equipmentId",
  "equipmentNickName",
  "instrumentAvailability",
  "isHazardous",
  "location",
  "serialNumber",
  "shouldAssisted",
  "status",
  "systemStatus"
];

/** Overwrites one grid property on a field, leaving the rest of the aspect intact. */
const overrideGrid = (field: FieldSchema | undefined, grid: Record<string, unknown>): FieldSchema =>
  ({ ...field, grid: { ...(field?.grid ?? {}), ...grid } }) as FieldSchema;

/**
 * Where booking diverges from the shared schema on a column both apps have.
 *
 * `siteName`: the shared schema filters it as a set, and instrument-repository offers that filter.
 * Booking does not — `listOfFieldsNoFilter` has suppressed it here for as long as the list has
 * existed, which makes sense in an app already scoped to the user's site. Preserved deliberately;
 * removing this override would add a site filter to every booking equipment grid.
 *
 * Visibility: every field is forced to its booking default, in both directions. Setting only the
 * visible ones would leave `manufacturer`, `materialNumber` and `siteName` shown, which the shared
 * schema defaults to but booking does not.
 */
const applyBookingOverrides = (schema: Schema): Schema => {
  const overridden: Schema = { ...schema };

  Object.keys(overridden).forEach((name) => {
    const field = overridden[name];
    if (!field?.grid) {
      return;
    }
    overridden[name] = overrideGrid(field, {
      defaultVisible: BOOKING_DEFAULT_VISIBLE.includes(name)
    });
  });

  overridden.siteName = overrideGrid(overridden.siteName, { filter: GridFilterTypes.NONE });

  return overridden;
};

/**
 * Builds the schema the booking equipment grids derive their columns from.
 *
 * @param client - Read client for the master data the shared schema resolves its enums from.
 * @returns The shared equipment schema extended and overridden for booking.
 */
export const prepareBookingEquipmentSchema = async (client: SchemaClient): Promise<Schema> => {
  const equipmentSchema = await prepareEquipmentSchema(client);
  // Overrides run over the merged schema, not just the shared half: two of the booking-only columns
  // (instrumentAvailability, displayQualificationStatus) are in the default view, and applying the
  // visibility pass first would leave them on their own declarations and hide them.
  return applyBookingOverrides({ ...equipmentSchema, ...bookingOnlyFields });
};

/** Exported for the characterization test, which asserts these against the current constants. */
export const bookingOnlyKeys = Object.keys(bookingOnlyFields);

/** Re-exported so the test can assert the render slots line up with BF's own renderer registry. */
export { GridRenderSlots };
